import { BulkApprovalBar } from "../BulkApprovalBar";
import { TruncatedTableValue } from "components/DesignSystem/tableColumnSizing";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import * as Mui from "@mui/material";
import { GlobalConfirmDialog } from "components/DesignSystem/sharedUiPatterns";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import { useSelector } from "react-redux";
import { customGetAPI, customAPI } from "services/utils/CustomAPI";
import {
  checkEditorPermission,
  checkFirstLevelApproverPermission,
  checkSecondLevelApproverPermission,
} from "services/utils/HelperFunction";
import { StatusBadge, displayFlexItem, getStatusFlow } from "services/constants";
import { StatusRow } from "components/CustomComponents/StatusRow";
import ApprovalAuditTrail from "components/DesignSystem/ApprovalAuditTrail";
import { TableEmptyState } from "components/CustomComponents/EmptyState";
import { Box } from "@mui/material";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { ApprovalDateRangeFilter } from "components/CustomComponents/ApprovalDateRangeFilter";
import { useSavedViewSelector } from "components/CustomComponents/useSavedViewSelector";
import { CorrectionApplyPanel } from "pages/MyZone/MyZoneApply/CorrectionShared/CorrectionApplyPanel";
import { BulkAttendanceCorrectionImport } from "./BulkAttendanceCorrectionImport";
import { Loader } from "components/Loader";
import { showToast } from "services/utils/Status";
import { useExpandablePanel } from "services/hooks/useExpandablePanel";
import {
  APP_BAR_OFFSET,
  designColors,
  designShell,
  designType,
  paginationFooter,
  rowsPerPageOptions,
} from "services/constants/designTokens";
import {
  bulkSelectionCheckboxSx,
  approveStickyCustomFilterBar,
  approveStickyFooterBar,
  approveTableHeadCell,
  approveTableBodyCell,
  NewRequestButton,
  ApproveTable,
} from "../approveUi";
import {
  AttendanceCorrectionStatusCards,
  canonicalAttendanceCorrectionStatus,
  attendanceCorrectionStatusVisual,
} from "./AttendanceCorrectionStatusCards";
import { AttendanceCorrectionDetailPanel } from "./AttendanceCorrectionDetailPanel";
import {
  ATTENDANCE_CORRECTION_API,
  ATTENDANCE_CORRECTION_HEADERS,
  toApproveTeamCorrectionRecord,
  toApproveFilterRow,
} from "./attendanceCorrectionApproveHelpers";
import moment from "moment";
import { resolveRangeLabel } from "../dateRangeUtils";

export const AttendanceCorrection = ({
  setReCallApi,
  reCallApi,
  startDate,
  endDate,
  dateRangeLabel: dateRangeLabelProp,
  handleStartDate,
  subModuleId,
}) => {
  const { domain, userDetails, topbarCompanyId, userScreenAccess } = useSelector(
    (state) => state.tokenReducer,
  );
  const [loading, setLoading] = useState(true);
  // Only the first load replaces the screen with the full-page Loader; later
  // refetches (status tile click, approve/reject) keep the page on screen
  // instead of flashing it like a page reload.
  const [hasLoaded, setHasLoaded] = useState(false);
  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [filterData, setFilterData] = useState([]);
  const [statusCardFilter, setStatusCardFilter] = useState("");
  const [selectedData, setSelectedData] = useState(null);
  const [viewDetails, setViewDetails] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [showBulkRejectDialog, setShowBulkRejectDialog] = useState(false);
  const [bulkRejectReason, setBulkRejectReason] = useState("");
  // Tracked by row id (not page index, and not a stored copy of the row) so
  // the expanded timeline always renders the current row -- e.g. the new
  // reason right after an edit -- and never a stale snapshot.
  const [expandedId, setExpandedId] = useState(null);
  const [isApplyOpen, setIsApplyOpen] = useState(false);
  const [isBulkImportOpen, setIsBulkImportOpen] = useState(false);
  // Initialized from (and kept in sync with) the Approval Center landing
  // page's own dateRangeLabel prop — previously hardcoded to "Last 7 Days"
  // regardless of the actual startDate/endDate this screen was handed, on
  // a fresh ?view= deep link or Team Workspace Home's "date" param. A
  // manual re-pick on this screen's own picker still overrides it locally.
  const [dateRangeLabel, setDateRangeLabel] = useState(
    dateRangeLabelProp || "Last 7 Days",
  );

  useEffect(() => {
    if (dateRangeLabelProp) setDateRangeLabel(dateRangeLabelProp);
  }, [dateRangeLabelProp]);

  const { expanded: panelExpanded, toggleExpanded: togglePanelExpanded } = useExpandablePanel(viewDetails);
  // Measures the sticky footer bar's own height so the table can reserve
  // that much bottom padding — otherwise its last row would sit hidden
  // behind the footer once the page scrolls that footer's sticky trigger
  // into effect. Ported from Administration's Users list / TeamZone
  // Approve > Leave, which already handle this correctly.
  const [footerNode, setFooterNode] = useState(null);
  const [footerHeight, setFooterHeight] = useState(0);
  const footerRef = useCallback((node) => setFooterNode(node), []);
  // Same measurement Administration's ListView/Companies' ListView use —
  // CustomFilter's chip row only appears once a filter/sort is applied, so
  // the sticky table head below it needs this measured (not guessed) to
  // know how far down to offset itself.
  const [customFilterNode, setCustomFilterNode] = useState(null);
  const [customFilterHeight, setCustomFilterHeight] = useState(0);
  const customFilterRef = useCallback((node) => setCustomFilterNode(node), []);

  useEffect(() => {
    if (!footerNode) return;
    const updateHeight = () => setFooterHeight(footerNode.offsetHeight);
    updateHeight();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(updateHeight);
    observer.observe(footerNode);
    return () => observer.disconnect();
  }, [footerNode]);

  useEffect(() => {
    if (!customFilterNode) return;
    const updateHeight = () =>
      setCustomFilterHeight(customFilterNode.offsetHeight);
    updateHeight();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(updateHeight);
    observer.observe(customFilterNode);
    return () => observer.disconnect();
  }, [customFilterNode]);

  useEffect(() => {
    const fetchAttendanceCorrections = async () => {
      const res = await customGetAPI(
        `${domain}${ATTENDANCE_CORRECTION_API}?user_id=${userDetails?.id}&company_id=${topbarCompanyId}&start_date=${startDate}&end_date=${endDate}`,
      );
      const rows = Array.isArray(res?.responseDetails)
        ? res.responseDetails
        : (res?.responseDetails?.results ?? []);
      const mappedRecords = rows.map(toApproveTeamCorrectionRecord);
      setRecords(mappedRecords);
      // Also seed the filtered view directly (same shape CustomFilter's own
      // effect would produce from filterInputRows) — that effect fires a
      // render later, so without this the table flashes "No Matching
      // Results" for a frame right as loading ends.
      setFilterData(mappedRecords.map(toApproveFilterRow));
      setLoading(false);
      setHasLoaded(true);
      setReCallApi(false);
    };
    if (startDate && endDate && reCallApi) {
      setLoading(true);
      fetchAttendanceCorrections();
    }
  }, [domain, endDate, reCallApi, setReCallApi, startDate, topbarCompanyId, userDetails?.id]);

  // Checks its own "Attendance Correction" submodule rather than sharing
  // WorkAdjustment's bucket. The backend's provisioning catalog has a live
  // data-entry typo here ("Attendace Correction", missing the "n" — see
  // sub_module_id 317) — checkEditorPermission accepts that spelling as an
  // alias for "Attendance Correction" (see SUB_MODULE_NAME_ALIASES in
  // services/utils/HelperFunction.js) so this doesn't read as "no access"
  // until the backend value itself gets corrected.
  const check_access = checkEditorPermission(userScreenAccess?.module_details, "Approvals", "Attendance Correction");

  // Raising a request (New Request / Bulk Upload) only needs editor, but
  // approving it on create needs a real approver flag -- same check as
  // AttendanceCorrectionDetailPanel. An editor-only user's requests stay
  // Pending for an approver to act on.
  const canApprove =
    checkFirstLevelApproverPermission(userScreenAccess?.module_details, "Approvals", "Attendance Correction") ||
    checkSecondLevelApproverPermission(userScreenAccess?.module_details, "Approvals", "Attendance Correction");

  const filterInputRows = useMemo(() => records.map(toApproveFilterRow), [records]);

  const handleFilteredDataChange = useCallback(
    (filtered = []) => {
      setFilterData(filtered);
      if (page * rowsPerPage >= filtered.length) setPage(0);
    },
    [page, rowsPerPage],
  );

  const statusCounts = useMemo(
    () =>
      records.reduce((acc, r) => {
        const status = canonicalAttendanceCorrectionStatus(r.status);
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      }, {}),
    [records],
  );

  const visibleData = useMemo(() => {
    if (!statusCardFilter) return filterData;
    return filterData.filter((r) => canonicalAttendanceCorrectionStatus(r.status) === statusCardFilter);
  }, [filterData, statusCardFilter]);

  const handleStatusCardClick = (key) => {
    clearSelection();
    setShowBulkRejectDialog(false);
    setPage(0);
    setExpandedId(null);
    setViewDetails(false);
    setSelectedData(null);
    setStatusCardFilter(key === "All" ? "" : key);
    // Previously only re-fetched on a date-range change or a manual page
    // refresh, so switching tabs could show stale results (e.g. a request
    // someone else just approved) until one of those happened. Force a fresh
    // fetch whenever a status tile is clicked.
    setReCallApi(true);
  };

  const { visibleHeaders, titleOverride } = useSavedViewSelector({
    domain,
    userId: userDetails?.id,
    subModuleId,
    storageKey: "team_attendance_correction_saved_views",
    fullHeaders: ATTENDANCE_CORRECTION_HEADERS,
    screenAccess: check_access,
    onViewChange: () => setPage(0),
  });

  const isSelected = (row) => selectedRowKeys.has(row.id);
  const pendingRows = useMemo(() => visibleData.filter((r) => r.status === "Pending"), [visibleData]);

  const toggleRow = (row) => {
    if (row.status !== "Pending") return;
    setSelectedRowKeys((prev) => {
      const next = new Set(prev);
      next.has(row.id) ? next.delete(row.id) : next.add(row.id);
      return next;
    });
  };

  const toggleAll = () => {
    const allSelected = pendingRows.length > 0 && pendingRows.every((r) => isSelected(r));
    setSelectedRowKeys(allSelected ? new Set() : new Set(pendingRows.map((r) => r.id)));
  };

  const isAllSelected = pendingRows.length > 0 && pendingRows.every((r) => isSelected(r));
  const isIndeterminate = selectedRowKeys.size > 0 && selectedRowKeys.size < pendingRows.length;

  const clearSelection = () => {
    setSelectedRowKeys(new Set());
    setBulkRejectReason("");
  };

  // PUT is keyed by (user_id, date) — one attendance record per call, not a
  // bulk `ids` list like Shift Change's endpoint — so bulk approve/reject
  // below fires one PUT per selected row via Promise.all.
  const putAttendanceCorrectionStatus = async (record, status, reason) => {
    const payload = {
      user_id: record.user_id,
      date: record.date,
      status,
      ...(status === "Approved" && { early_exit: Boolean(record.early_exit) }),
      ...(status === "Rejected" && { rejected_reason: reason || "" }),
      // No dedicated cancel-an-approved-attendance-correction endpoint
      // exists yet, same gap as Comp Off — this reuses the same
      // approve/reject endpoint with status: "Cancelled" as a first
      // attempt, needs verifying against the real backend.
      ...(status === "Cancelled" && { cancelled_reason: reason || "" }),
    };
    return customAPI(`${domain}${ATTENDANCE_CORRECTION_API}`, "PUT", payload);
  };

  const putAttendanceCorrectionStatusBulk = async (ids, status, reason) => {
    const targets = records.filter((r) => ids.has(r.id));
    const responses = await Promise.all(
      targets.map((record) => putAttendanceCorrectionStatus(record, status, reason)),
    );
    return { ok: responses.length > 0 && responses.every((res) => res?.ok), responses };
  };

  const handleApprove = async (record) => {
    setActionLoading(true);
    const response = await putAttendanceCorrectionStatus(record, "Approved");
    if (response?.ok) {
      showToast("success", response?.responseDetails?.message || "Attendance correction request approved.");
      setViewDetails(false);
      setReCallApi(true);
    } else {
      showToast("error", response?.message || "Couldn't approve the attendance correction request. Please try again.");
    }
    setActionLoading(false);
  };

  const handleReject = async (record, reason) => {
    setActionLoading(true);
    const response = await putAttendanceCorrectionStatus(record, "Rejected", reason);
    if (response?.ok) {
      showToast("success", response?.responseDetails?.message || "Attendance correction request rejected.");
      setViewDetails(false);
      setReCallApi(true);
    } else {
      showToast("error", response?.message || "Couldn't reject the attendance correction request. Please try again.");
    }
    setActionLoading(false);
  };

  const handleCancel = async (record, reason) => {
    setActionLoading(true);
    const response = await putAttendanceCorrectionStatus(record, "Cancelled", reason);
    if (response?.ok) {
      showToast("success", response?.responseDetails?.message || "Attendance correction request cancelled.");
      setViewDetails(false);
      setReCallApi(true);
    } else {
      showToast("error", response?.message || "Couldn't cancel the attendance correction request. Please try again.");
    }
    setActionLoading(false);
  };

  const handleBulkApprove = async () => {
    if (!selectedRowKeys.size) return;
    setBulkLoading(true);
    const { ok } = await putAttendanceCorrectionStatusBulk(selectedRowKeys, "Approved");
    if (ok) {
      showToast("success", "Records approved.");
      clearSelection();
      setReCallApi(true);
    } else {
      showToast("error", "Failed to approve");
    }
    setBulkLoading(false);
  };

  const handleBulkReject = async () => {
    if (!selectedRowKeys.size) return;
    setBulkLoading(true);
    const { ok } = await putAttendanceCorrectionStatusBulk(selectedRowKeys, "Rejected", bulkRejectReason);
    if (ok) {
      showToast("success", "Records rejected.");
      clearSelection();
      setShowBulkRejectDialog(false);
      setReCallApi(true);
    } else {
      showToast("error", "Failed to reject");
    }
    setBulkLoading(false);
  };

  const handleAttendanceCorrectionDateRange = (rangeStart, rangeEnd, rangeLabel) => {
    handleStartDate(rangeStart, rangeEnd);
    setDateRangeLabel(resolveRangeLabel(rangeLabel));
  };

  const reportMetadata = useMemo(
    () => ({
      "User Name": userDetails?.username || userDetails?.user_name || "N/A",
      Type: "Attendance Correction",
      "Report Generated On": moment().format("DD-MM-YYYY HH:mm:ss"),
    }),
    [userDetails],
  );

  return loading && !hasLoaded ? (
    <Loader />
  ) : (
    <React.Fragment>
      <Box sx={{ flexGrow: 1, width: "100%", overflow: "visible" }}>
        <Box
          sx={{
            mb: 2,
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "flex-start", md: "center" },
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box>
            <Mui.Typography sx={designType.pageTitle}>Attendance Correction</Mui.Typography>
            <Mui.Typography sx={designType.pageSubtitle}>
              Review and manage employees' attendance correction requests
            </Mui.Typography>
          </Box>

          <Mui.Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
            <ApprovalDateRangeFilter
              startDate={startDate}
              endDate={endDate}
              dateRangeLabel={dateRangeLabel}
              onChange={handleAttendanceCorrectionDateRange}
              includeCurrentDate
              showToday
            />
            {check_access ? (
              <Mui.Button
                variant="outlined"
                startIcon={<UploadFileOutlinedIcon sx={{ fontSize: 18 }} />}
                onClick={() => setIsBulkImportOpen(true)}
              >
                Bulk Upload
              </Mui.Button>
            ) : null}
            <NewRequestButton
              hasAccess={check_access}
              onClick={() => setIsApplyOpen(true)}
            />
          </Mui.Stack>
        </Box>

        <AttendanceCorrectionStatusCards
          counts={statusCounts}
          activeFilter={statusCardFilter}
          onCardClick={handleStatusCardClick}
        />

        <Box ref={customFilterRef} sx={approveStickyCustomFilterBar}>
          <CustomFilter
            title="Attendance Correction Requests"
            titleOverride={titleOverride}
            filterHeaders={visibleHeaders}
            data={filterInputRows}
            filteredData={visibleData}
            onFilteredDataChange={handleFilteredDataChange}
            screenAccess={check_access}
            showSaveButton={true}
            hideCount={true}
            downloadFormats={["excel", "pdf", "csv"]}
            includeMetadata={true}
            metadata={reportMetadata}
             labeledToolbar={true}
          />
        </Box>

        <BulkApprovalBar
          selectedCount={statusCardFilter === "Pending" ? selectedRowKeys.size : 0}
          loading={bulkLoading}
          onReject={() => setShowBulkRejectDialog(true)}
          onApprove={handleBulkApprove}
        />

        <Box sx={{ ...designShell.sectionCard, mt: 1.5, borderRadius: 0 }}>
          <Mui.TableContainer
            sx={{
              width: "100%",
              overflowX: { xs: "auto", sm: "visible" },
              overflowY: { xs: "auto", sm: "visible" },
              pb: footerHeight ? `${footerHeight}px` : 0,
            }}
          >
            <ApproveTable stickyHeader sx={{ width: "100%", tableLayout: "fixed" }}>
              <Mui.TableHead sx={designShell.tableStickyHead}>
                <Mui.TableRow sx={designShell.tableStickyHeadRow}>
                  {statusCardFilter === "Pending" && (
                    <Mui.TableCell
                      padding="checkbox"
                      sx={{
                        ...approveTableHeadCell,
                        top: APP_BAR_OFFSET + customFilterHeight,
                      }}
                    >
                      {statusCardFilter === "Pending" && pendingRows.length > 0 && (
                        <Mui.Checkbox
                          size="small"
                          sx={bulkSelectionCheckboxSx}
                          checked={isAllSelected}
                          indeterminate={isIndeterminate}
                          onChange={toggleAll}
                        />
                      )}
                    </Mui.TableCell>
                  )}
                  <Mui.TableCell
                    sx={{
                      ...approveTableHeadCell,
                      top: APP_BAR_OFFSET + customFilterHeight,
                      width: 40,
                    }}
                  />
                  {visibleHeaders?.map((value) => (
                    <Mui.TableCell
                      key={value.key}
                      sx={{
                        ...approveTableHeadCell,
                        top: APP_BAR_OFFSET + customFilterHeight,
                      }}
                    >
                      <TruncatedTableValue value={value?.name} />
                    </Mui.TableCell>
                  ))}
                </Mui.TableRow>
              </Mui.TableHead>
              <Mui.TableBody>
                {visibleData?.length === 0 ? (
                  <TableEmptyState dataLength={filterInputRows} colSpan={visibleHeaders.length + 1 + (statusCardFilter === "Pending" ? 1 : 0)} />
                ) : (
                  visibleData
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((value) => (
                      <React.Fragment key={value.id}>
                        <Mui.TableRow
                          onClick={() => {
                            setSelectedData(value);
                            setViewDetails(true);
                          }}
                          sx={{
                            cursor: "pointer",
                            ...designShell.tableRowHover,
                            ...(viewDetails && value.id === selectedData?.id ? designShell.neutralSelectedRow : {}),
                            "&:last-child td": { borderBottom: "none" },
                          }}
                        >
                          {statusCardFilter === "Pending" && (
                            <Mui.TableCell
                              padding="checkbox"
                              sx={approveTableBodyCell}
                              onClick={(event) => event.stopPropagation()}
                            >
                              {statusCardFilter === "Pending" && value.status === "Pending" && (
                                <Mui.Checkbox
                                  size="small"
                                  sx={bulkSelectionCheckboxSx}
                                  checked={isSelected(value)}
                                  onChange={() => toggleRow(value)}
                                />
                              )}
                            </Mui.TableCell>
                          )}
                          <Mui.TableCell
                            sx={approveTableBodyCell}
                            onClick={(event) => {
                              event.stopPropagation();
                              setExpandedId((prev) => (prev === value.id ? null : value.id));
                            }}
                            aria-label={expandedId === value.id ? "Collapse row" : "Expand row"}
                          >
                            <ArrowRightIcon
                              sx={{
                                color: designColors.slate500,
                                cursor: "pointer",
                                transition: "transform 0.2s ease",
                                transform: expandedId === value.id ? "rotate(90deg)" : "rotate(0deg)",
                              }}
                            />
                          </Mui.TableCell>
                          {visibleHeaders.map((head) => (
                            <Mui.TableCell key={head.key} data-label={head.name} sx={approveTableBodyCell}>
                              {head.key === "employee_name" ? (
                                <Mui.Stack direction="row" spacing={1} alignItems="center">
                                  <Mui.Avatar
                                    sx={{ width: 28, height: 28 }}
                                    alt={value?.employee_name?.charAt(0).toUpperCase()}
                                    src={value?.image_url}
                                  />
                                  <Mui.Typography sx={{ fontSize: 14 }}>{value?.employee_name || "N/A"}</Mui.Typography>
                                </Mui.Stack>
                              ) : head.key === "status" ? (
                                <Mui.Box sx={displayFlexItem}>
                                  <StatusBadge
                                    color={
                                      value?.color_code ||
                                      attendanceCorrectionStatusVisual(value?.status).accent
                                    }
                                    label={value?.status || "N/A"}
                                  />
                                </Mui.Box>
                              ) : (
                                (<TruncatedTableValue value={value[head.key] ?? "N/A"} />)
                              )}
                            </Mui.TableCell>
                          ))}
                        </Mui.TableRow>

                        {expandedId === value.id && (
                          <Mui.TableRow>
                            <Mui.TableCell
                              colSpan={visibleHeaders.length + 1 + (statusCardFilter === "Pending" ? 1 : 0)}
                              sx={{ p: 2, borderBottom: `1px solid ${designColors.slate200}` }}
                            >
                              {getStatusFlow(value)[value?.status]?.map((item, i, arr) => (
                                <StatusRow
                                  key={i}
                                  status={item.status}
                                  user={item.user}
                                  date={item.date}
                                  showConnector={i !== arr.length - 1}
                                />
                              ))}
                              <ApprovalAuditTrail record={value} compact />
                            </Mui.TableCell>
                          </Mui.TableRow>
                        )}
                      </React.Fragment>
                    ))
                )}
              </Mui.TableBody>
            </ApproveTable>
          </Mui.TableContainer>
          <Box ref={footerRef} sx={approveStickyFooterBar}>
            {paginationFooter({
              page,
              rowsPerPage,
              total: visibleData?.length || 0,
              itemLabel: "attendance correction requests",
              onPageChange: setPage,
              onRowsPerPageChange: (event) => {
                setRowsPerPage(parseInt(event.target.value, 10));
                setPage(0);
              },
              rowsPerPageOptions,
            })}
          </Box>
        </Box>
      </Box>

      <AttendanceCorrectionDetailPanel
        open={viewDetails}
        onClose={() => setViewDetails(false)}
        record={selectedData}
        checkAccess={check_access}
        onApprove={handleApprove}
        onReject={handleReject}
        onCancel={handleCancel}
        onUpdated={() => setReCallApi(true)}
        actionLoading={actionLoading}
        expanded={panelExpanded}
        onToggleExpand={togglePanelExpanded}
      />

      {isApplyOpen ? (
        <CorrectionApplyPanel
          open={isApplyOpen}
          onClose={() => setIsApplyOpen(false)}
          onCreate={() => {
            setIsApplyOpen(false);
            setReCallApi(true);
          }}
          title="Attendance Correction Request"
          subtitle="Raise a correction request on behalf of an employee."
          showEmployeePicker
          approveOnCreate={canApprove}
        />
      ) : null}

      {isBulkImportOpen ? (
        <BulkAttendanceCorrectionImport
          open={isBulkImportOpen}
          onClose={() => setIsBulkImportOpen(false)}
          setReCallApi={setReCallApi}
          approveOnCreate={canApprove}
        />
      ) : null}

      <GlobalConfirmDialog
        open={showBulkRejectDialog}
        title="Reject Selected Requests"
        onCancel={() => {
          setShowBulkRejectDialog(false);
          setBulkRejectReason("");
        }}
        onConfirm={handleBulkReject}
        confirmLabel="Reject"
        cancelLabel="Cancel"
        loading={bulkLoading}
        danger
        reasonValue={bulkRejectReason}
        onReasonChange={setBulkRejectReason}
        reasonPlaceholder="Enter a reason for rejection"
        reasonMinRows={2}
        maxWidth="sm"
      >
        <Mui.Typography sx={{ fontSize: 14, lineHeight: 1.5, mb: 2 }}>
          Reason (optional) - applied to all selected attendance correction requests.
        </Mui.Typography>
      </GlobalConfirmDialog>
    </React.Fragment>
  );
};

export default AttendanceCorrection;
